import posthog from 'posthog-js';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from './DataAccessUtils';

export const SITE_SURVEY_ANSWER_TYPE = {
	MULTIPLE_CHOICE: 'multipleChoice',
	FREE_TEXT: 'textbox',
	TASK_LIST: 'taskList',
	CHECKLIST: 'checklist',
};

// Walkthrough PDF export variants. Mirrors WalkthroughPdfExportMode on the API; the values travel as
// the `export_mode` query param and come back on the pdf_status response.
export const PDF_EXPORT_MODES = {
	STANDARD: 'standard',
	LARGE_PHOTOS: 'large_photos',
};

// Whether the larger-photo option is offered is decided by the API and published on the walkthrough as
// `pdfLargePhotosSupported`, alongside the `pdfExportableImageCount` the tooltip quotes. Reading the
// server's verdict rather than re-deriving it here keeps the disabled state and the server-side guard
// from drifting apart — the count rule (one box per attachment per task, images only) lives in one place.
//
// Read off the object directly rather than through nullSafeGet/nullSafeGetOrElse: both are truthiness
// based (`xs && xs[x] ? xs[x] : elseVal`), so they cannot carry a legitimate `false` and would hand back
// the fallback for exactly the walkthroughs this is meant to catch. Only an explicit `false` disables the
// option, so a walkthrough loaded before the API published the field still offers it.
export const isLargePhotoPdfSupported = (walkthrough) =>
	(walkthrough || {}).pdfLargePhotosSupported !== false;

export const walkthroughPhotoCount = (walkthrough) =>
	(walkthrough || {}).pdfExportableImageCount || 0;

// True once the loaded walkthrough is the one the route asked for and nothing is in flight. Redux keeps
// the previously-viewed walkthrough in `detail` until a new fetch resolves, so an emptiness check would
// report ready while holding another walkthrough's photo count. Ids are compared as strings because the
// route param is one and the entity's is a number; read directly rather than through nullSafeGet, which
// is truthiness based and would not survive an id of 0.
export const isWalkthroughDetailReady = (walkthrough, walkthroughId, fetching) =>
	!fetching && String((walkthrough || {}).id || '') === String(walkthroughId || '');

// Each variant caches its own link on the walkthrough, so anything reading "the PDF link" has to say
// which one it means.
export const pdfLinkFieldFor = (exportMode) =>
	exportMode === PDF_EXPORT_MODES.LARGE_PHOTOS ? 'pdfLinkLargePhotos' : 'pdfLink';

// Quoted in the larger-photo option's label so the ceiling is visible before the option is picked, and
// again in the explanation below when it has been exceeded. Display only — whether the option is actually
// offered is the API's call, published as `pdfLargePhotosSupported` and read by isLargePhotoPdfSupported
// above. Kept here so the label and the explanation cannot end up quoting different numbers.
export const LARGE_PHOTO_PDF_IMAGE_LIMIT = 150;

// Replaces the larger-photo option's subtitle in the export modal when the option is unavailable, so the
// card says why it cannot be picked and what to pick instead. Names the other option by its label
// ("Compact photos") rather than "the standard PDF" — `standard` is the wire value, not something the user
// sees anywhere in the modal.
export const LARGE_PHOTO_PDF_DISABLED_REASON = (photoCount) =>
	`This walkthrough has ${photoCount} photos, more than the ${LARGE_PHOTO_PDF_IMAGE_LIMIT} the larger ` +
	`size allows. Please export compact photos instead.`;

export const CHOICE_FOLLOW_UP_ACTION = {
	REQUIRE_COMMENT: 'requireComment',
	REQUIRE_PHOTO: 'requirePhoto',
	REQUIRE_WORK_ORDER: 'requireWorkOrder',
};

export type ChoiceFollowUpAction = 'requireComment' | 'requirePhoto' | 'requireWorkOrder';

// The same follow-up actions can now also be set on a task itself (independent of
// answer type), not just on individual choices. The values are identical, so this
// is just a clearer alias for the task-level usage that mirrors the backend rename
// from ChoiceFollowUpAction -> FollowUpAction.
export type FollowUpAction = ChoiceFollowUpAction;

export const FOLLOW_UP_ACTION_OPTIONS = [
	{ value: CHOICE_FOLLOW_UP_ACTION.REQUIRE_COMMENT, label: 'Require comment' },
	{ value: CHOICE_FOLLOW_UP_ACTION.REQUIRE_PHOTO, label: 'Require photo' },
	{ value: CHOICE_FOLLOW_UP_ACTION.REQUIRE_WORK_ORDER, label: 'Require work order' },
];

// Bidirectional maps between a follow-up action key (stored on the choice) and
// its human-readable label, so the same labels drive the read-only UI and the
// spreadsheet export/import round-trip.
const FOLLOW_UP_ACTION_LABEL_BY_VALUE = FOLLOW_UP_ACTION_OPTIONS.reduce((accumulator, option) => {
	accumulator[option.value] = option.label;
	return accumulator;
}, {});

const FOLLOW_UP_ACTION_VALUE_BY_LABEL = FOLLOW_UP_ACTION_OPTIONS.reduce((accumulator, option) => {
	accumulator[option.label.toLowerCase()] = option.value;
	return accumulator;
}, {});

// Map stored follow-up action keys to their display labels, dropping any
// unknown keys. Used by both the read-only task list and the export.
export const getFollowUpActionLabels = (followUpActions) =>
	(followUpActions || [])
		.map((action) => nullSafeGet(action, FOLLOW_UP_ACTION_LABEL_BY_VALUE))
		.filter(Boolean);

// Short labels for the compact read-only indicator (vs. the full "Require ..."
// labels used by chips and the export).
const FOLLOW_UP_ACTION_SHORT_LABEL_BY_VALUE = {
	[CHOICE_FOLLOW_UP_ACTION.REQUIRE_COMMENT]: 'Comment',
	[CHOICE_FOLLOW_UP_ACTION.REQUIRE_PHOTO]: 'Photo',
	[CHOICE_FOLLOW_UP_ACTION.REQUIRE_WORK_ORDER]: 'Work order',
};

// Compact inline indicator for a task/choice's follow-up actions, e.g.
// "(*Comment, *Photo required)". Returns '' when there are none so callers can
// render it conditionally.
export const getFollowUpActionIndicator = (followUpActions) => {
	const shortLabels = (followUpActions || [])
		.map((action) => nullSafeGet(action, FOLLOW_UP_ACTION_SHORT_LABEL_BY_VALUE))
		.filter(Boolean);
	return shortLabels.length > 0 ? `(*${shortLabels.join(', *')} required)` : '';
};

// Multiple-choice answers historically store the formatted display string
// ("value (description)") rather than the raw value, so match against either
// form to resolve the selected Choice for follow-up enforcement.
export const findChoiceForAnswer = (answer, multipleChoicesWithLabel) => {
	if (!answer || !Array.isArray(multipleChoicesWithLabel)) return null;
	const matched = multipleChoicesWithLabel.find((choice) => {
		const formatted = `${choice.value}${choice.description ? ` (${choice.description})` : ''}`;
		return choice.value === answer || formatted === answer;
	});
	return matched || null;
};

export const getSiteSurveyAnswerTypeDisplayName = (key) => {
	const displayNames = {
		[SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE]: 'Multiple Choice',
		[SITE_SURVEY_ANSWER_TYPE.FREE_TEXT]: 'Free Text',
		[SITE_SURVEY_ANSWER_TYPE.TASK_LIST]: 'Capture Tasks',
		[SITE_SURVEY_ANSWER_TYPE.CHECKLIST]: 'Checklist',
	};

	return nullSafeGetOrElse(key, displayNames, key);
};

// Export each choice as "value:description [Require photo; Require comment]".
// The trailing bracketed segment is omitted when the choice has no follow-up
// actions, keeping simple choices identical to the legacy format.
export const getMultipleChoiseWithLabelForExport = (multipleChoicesWithLabel) =>
	multipleChoicesWithLabel
		.map((choice) => {
			const base = `${choice.value}${choice.description ? `:${choice.description}` : ''}`;
			const actionLabels = getFollowUpActionLabels(choice.followUpActions);
			return actionLabels.length > 0 ? `${base} [${actionLabels.join('; ')}]` : base;
		})
		.join(',');

export const getMultipleChoiseWithLabelForBE = (stringValue = '') =>
	stringValue.split(',').map((choice) => {
		// Peel off a trailing "[Require photo; Require comment]" block first so the
		// follow-up labels never leak into the value/description split below.
		const followUpActions = [];
		let valueAndDescriptionPart = choice;
		const actionsMatch = choice.match(/\[([^\]]*)\]\s*$/);
		if (actionsMatch) {
			valueAndDescriptionPart = choice.slice(0, actionsMatch.index);
			actionsMatch[1]
				.split(';')
				.map((label) => label.trim().toLowerCase())
				.filter(Boolean)
				.forEach((label) => {
					const actionValue = nullSafeGet(label, FOLLOW_UP_ACTION_VALUE_BY_LABEL);
					if (actionValue) followUpActions.push(actionValue);
				});
		}
		const valueAndDescription = valueAndDescriptionPart.split(':');
		const value = (valueAndDescription[0] || '').trim();
		const description = nullSafeGet('1', valueAndDescription) ? valueAndDescription[1].trim() : '';
		return {
			value,
			...(description && { description }),
			...(followUpActions.length > 0 && { followUpActions }),
		};
	});

// Task-level follow-up actions share the same value set and labels as the
// per-choice ones, but live in their own grid column / CSV column. Export them as
// a "; "-joined list of labels (empty string when there are none).
export const getFollowUpActionsForExport = (task) =>
	getFollowUpActionLabels(nullSafeGet('followUpActions', task)).join('; ');

// Normalize a Follow-up actions cell value into the BE shape { followUpActions: [...] }
// (or {} when empty). Accepts both the grid-edited array of action values and the
// "; "-joined label string produced by a CSV upload, mapping labels back to values.
export const getFollowUpActionsForBE = (value) => {
	let actions = [];
	if (Array.isArray(value)) {
		actions = value.filter((action) => nullSafeGet(action, FOLLOW_UP_ACTION_LABEL_BY_VALUE));
	} else if (typeof value === 'string' && value.trim().length > 0) {
		value
			.split(';')
			.map((label) => label.trim().toLowerCase())
			.filter(Boolean)
			.forEach((label) => {
				const actionValue = nullSafeGet(label, FOLLOW_UP_ACTION_VALUE_BY_LABEL);
				if (actionValue) actions.push(actionValue);
			});
	}
	return actions.length > 0 ? { followUpActions: actions } : {};
};

// Follow-up actions on a task can be configured at two levels: against the task
// as a whole (the task-level "Follow-up actions" column) or against individual
// multiple-choice options. The two are mutually exclusive — a task uses one level
// or the other, never both. The two predicates below answer "are follow-up
// actions present at this level?" so a consumer (e.g. the import grid) can decide
// whether to disable the opposite control. They accept both the grid-edited form
// (arrays) and the CSV string form, mirroring the *ForBE adapters.

// True when any multiple-choice option carries its own follow-up actions.
export const choiceLevelFollowUpActionsExist = (answerOptions) => {
	const choices = Array.isArray(answerOptions)
		? answerOptions
		: typeof answerOptions === 'string' && answerOptions.trim().length > 0
		? getMultipleChoiseWithLabelForBE(answerOptions)
		: [];
	return choices.some(
		(choice) =>
			Array.isArray(nullSafeGet('followUpActions', choice)) && choice.followUpActions.length > 0
	);
};

// True when the task-level follow-up actions cell carries any actions.
export const taskLevelFollowUpActionsExist = (followUpActions) => {
	const actions = nullSafeGet('followUpActions', getFollowUpActionsForBE(followUpActions));
	return Array.isArray(actions) && actions.length > 0;
};

export const getChecklistTasksForExport = (checklistTasks) =>
	(checklistTasks || []).map((item) => item.title).join(';');

// Multiple-choice options and checklist items are stored under separate task
// keys but share a single "Answer Options" column in the grid and CSV. The three
// adapters below convert between that single cell value and the per-type keys at
// each boundary, so the column stays unified end to end (export, import, edit,
// submit). To support a new answer type's options, extend each adapter's switch.

// Mirrors faltfile_utils' COMMA_SEPARATED_FORMAT, kept as a local literal so this
// widely-imported util doesn't pull in the Flatfile SDK. Allows the base
// "value:description" choices plus an optional trailing follow-up actions block,
// e.g. "yes:Yes [Require photo; Require comment]".
const MULTIPLE_CHOICE_VALUES_REGEX = /[a-zA-Z0-9_ :;[\]]+(,[a-zA-Z0-9_ :;[\]]+)*$/;

// Compose a task's options into one Answer Options cell string for CSV export.
export const getAnswerOptionsForExport = (task) => {
	if (task.questionType === SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE) {
		return task.multipleChoicesWithLabel
			? getMultipleChoiseWithLabelForExport(task.multipleChoicesWithLabel)
			: '';
	}
	if (task.questionType === SITE_SURVEY_ANSWER_TYPE.CHECKLIST) {
		return task.checklistTasks ? getChecklistTasksForExport(task.checklistTasks) : '';
	}
	return '';
};

// Seed the Answer Options cell (array form) from an existing task when opening
// the edit grid, picking the source key that matches the task's answer type.
export const getAnswerOptionsSourceValue = (task) => {
	if (task.questionType === SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE) {
		return task.multipleChoicesWithLabel || undefined;
	}
	if (task.questionType === SITE_SURVEY_ANSWER_TYPE.CHECKLIST) {
		return task.checklistTasks || undefined;
	}
	return undefined;
};

// Split the single Answer Options cell back into the per-type backing key for
// the submitted payload. Accepts arrays (grid-edited) or the CSV string form,
// and returns an empty object for answer types that carry no options.
export const getAnswerOptionsForBE = (questionType, answerOptions) => {
	if (questionType === SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE) {
		if (Array.isArray(answerOptions)) {
			return { multipleChoicesWithLabel: answerOptions };
		}
		if (answerOptions) {
			return { multipleChoicesWithLabel: getMultipleChoiseWithLabelForBE(answerOptions) };
		}
	}
	if (questionType === SITE_SURVEY_ANSWER_TYPE.CHECKLIST) {
		if (Array.isArray(answerOptions)) {
			const cleaned = answerOptions
				.filter((item) => item && item.title && item.title.trim().length > 0)
				.map((item, orderingId) => ({ orderingId, title: item.title.trim() }));
			return cleaned.length > 0 ? { checklistTasks: cleaned } : {};
		}
		if (answerOptions) {
			return { checklistTasks: getChecklistTasksForBE(answerOptions) };
		}
	}
	return {};
};

// Validate the Answer Options cell against the current answer type. Mirrors the
// per-type rules the grid enforces: a valid multiple-choice CSV string, and at
// least one option / checklist item for those answer types.
export const siteSurveyTaskRecordHook = (record) => {
	const questionType = record.get('questionType');
	const answerOptions = record.get('answerOptions');

	// Answer types without options never carry an Answer Options value.
	if (
		questionType === SITE_SURVEY_ANSWER_TYPE.FREE_TEXT ||
		questionType === SITE_SURVEY_ANSWER_TYPE.TASK_LIST
	) {
		record.set('answerOptions', '');
		return record;
	}

	if (questionType === SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE) {
		// Only the CSV string form is pattern-checked; grid edits arrive as arrays
		// (typeof !== 'string') and skip the regex. Allows "value:description"
		// choices plus an optional trailing "[Require photo; ...]" follow-up block.
		if (
			typeof answerOptions === 'string' &&
			answerOptions &&
			!MULTIPLE_CHOICE_VALUES_REGEX.test(answerOptions)
		) {
			record.addError('answerOptions', 'Values should be comma separated. Ex: 1:low,2:high');
		}
		const hasOptions = Array.isArray(answerOptions)
			? answerOptions.some(
					(item) => item && typeof item.value === 'string' && item.value.trim().length > 0
			  )
			: typeof answerOptions === 'string' && answerOptions.trim().length > 0;
		if (!hasOptions) {
			record.addError('answerOptions', 'Add at least one answer option.');
		}
	}

	if (questionType === SITE_SURVEY_ANSWER_TYPE.CHECKLIST) {
		const hasItems = Array.isArray(answerOptions)
			? answerOptions.some(
					(item) => item && typeof item.title === 'string' && item.title.trim().length > 0
			  )
			: typeof answerOptions === 'string' && answerOptions.trim().length > 0;
		if (!hasItems) {
			record.addError('answerOptions', 'Add at least one checklist item.');
		}
	}

	return record;
};

export const getChecklistTasksForBE = (stringValue = '') =>
	stringValue
		.split(';')
		.map((title) => title.trim())
		.filter(Boolean)
		.map((title, orderingId) => ({ orderingId, title }));

export const validateForDuplicateTitlesUnderSameGroup = (records) => {
	const { duplicateTitles } = records.reduce(
		(acc, record) => {
			const { uniqueKeys, duplicateTitles } = acc;
			const key = `${nullSafeGetOrElse('groupTitle', record, '')}-${nullSafeGetOrElse(
				'title',
				record,
				''
			)}`;
			return {
				...acc,
				...(uniqueKeys.includes(key)
					? {
							duplicateTitles: [...duplicateTitles, record.title],
					  }
					: {
							uniqueKeys: [...uniqueKeys, key],
					  }),
			};
		},
		{ uniqueKeys: [], duplicateTitles: [] }
	);

	return duplicateTitles.length > 0
		? (duplicateTitles.length > 1
				? `Titles ${duplicateTitles
						.map((_) => `"${_}"`)
						.join(', ')} are repeated under same group. `
				: `Title "${duplicateTitles[0]}" is repeated under same group. `) +
				'Please fix it or remove the duplicate to proceed'
		: '';
};

export const isWalthroughTaskAnswered = (task) => {
	const answerExist = task.answer && task.answer.trim();
	const taskListAnswerExist = !!nullSafeGet('taskListAnswer.0', task);
	const checklistAnswerExist = nullSafeGetOrElse('checklistAnswer', task, []).some(
		(checklistItem) => nullSafeGetOrElse('answer', checklistItem, 'false') === 'true'
	);

	return answerExist || taskListAnswerExist || checklistAnswerExist;
};

export const isTaskListEnabled = () => {
	return true || posthog.isFeatureEnabled('walkthrough-task-list-enabled');
};

// Missing `score` means unscored; `score: 0` is valid — use nullSafeGetWithZero + presence checks, not truthiness.
export const validScoreAvailable = (scoreObject) => {
	const score = nullSafeGetWithZero('score', scoreObject);
	const maxPossibleScore = nullSafeGetWithZero('maxPossibleScore', scoreObject);
	return score !== undefined && maxPossibleScore !== undefined;
};
